"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, BookOpen, Calendar, Clock, Download, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface FlagshipReport {
  id: string;
  title: string;
  subtitle: string;
  excerpt: string;
  coverImage: string;
  coverSrcset: string;
  coverSizes: string;
  category: string;
  format: string;
  readTime: string;
  publishDate: string;
  pagesCount: string;
  href: string;
}
interface ResearchBriefing {
  id: string;
  title: string;
  category: string;
  date: string;
  fileSize: string;
  href: string;
}
const flagshipReports: FlagshipReport[] = [{
  id: "report-1",
  title: "The 2025 enterprise software procurement benchmark",
  subtitle: "Analysis of 420 B2B technology buying committees",
  excerpt: "An exhaustive investigation into how executive evaluation cycles, budget authority shifts, and vendor accountability models have adapted to decentralized decision teams.",
  coverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/research_report_procurement_cover.webp",
  coverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/research_report_procurement_cover.webp-wvc-srcset",
  coverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/research_report_procurement_cover.webp-wvc-sizes",
  category: "Market Insights",
  format: "Full Report (PDF)",
  readTime: "24 min read",
  publishDate: "Q1 2025 Edition",
  pagesCount: "68 pages",
  href: "/ResearchReportsPage"
}, {
  id: "report-2",
  title: "AI integration in financial operations & forecasting",
  subtitle: "Quantitative study across 180 mid-market institutions",
  excerpt: "Examining practical risk frameworks, compliance verification requirements, and the measured return on algorithmic modeling in regulated commercial environments.",
  coverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/research_report_ai_finance_cover.png",
  coverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/research_report_ai_finance_cover.png-wvc-srcset",
  coverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/research_report_ai_finance_cover.png-wvc-sizes",
  category: "B2B Research Reports",
  format: "Research Monograph",
  readTime: "18 min read",
  publishDate: "January 2025",
  pagesCount: "52 pages",
  href: "/ResearchReportsPage"
}];
const researchBriefings: ResearchBriefing[] = [{
  id: "briefing-1",
  title: "Cloud infrastructure spend reallocation in mature B2B firms",
  category: "Strategic Brief",
  date: "Feb 12, 2025",
  fileSize: "1.4 MB",
  href: "/IndustryInsightsPage"
}, {
  id: "briefing-2",
  title: "B2B sales cycle duration metrics: 2024–2025 longitudinal data",
  category: "Data Briefing",
  date: "Jan 28, 2025",
  fileSize: "2.1 MB",
  href: "/IndustryInsightsPage"
}, {
  id: "briefing-3",
  title: "Data governance protocols for cross-border enterprise partnerships",
  category: "Compliance Brief",
  date: "Jan 15, 2025",
  fileSize: "1.8 MB",
  href: "/IndustryInsightsPage"
}];
export default function Research() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="featured-research" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Masthead Rule Header */}
        <div className="mb-10 sm:mb-14">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="h-2 w-2 rounded-full bg-accent" />
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Editorial Spotlight
                </span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-[40px] font-semibold text-foreground tracking-tight leading-tight">
                Flagship industry research
              </h2>
            </div>
            <Button variant="outline" size="sm" asChild className="self-start sm:self-auto rounded-sm border-border text-foreground hover:bg-muted font-sans font-medium">
              <Link to="/ResearchReportsPage">
                <span>View all reports</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>

          {/* Masthead Dual-Weight Rule */}
          <div className="w-full space-y-[2px]" aria-hidden="true">
            <div className="h-[2px] w-full bg-foreground" />
            <div className="h-[1px] w-full bg-border" />
          </div>
        </div>

        {/* Content Layout: 2 Flagship Reports (8-cols) + 1 Briefings Stream (4-cols) */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Flagship Research Reports Column */}
          <div className="lg:col-span-8 flex flex-col gap-8">
            {flagshipReports.map((report, i) => <motion.article key={report.id} data-index={i} variants={itemVariants} className="group rounded-md border border-border bg-card p-6 sm:p-8 shadow-sm transition-[border-color,box-shadow] duration-250 hover:border-foreground/30 hover:shadow-md">
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-6 sm:gap-8 items-center">
                  {/* Publication Spine Cover (3:4 Ratio) */}
                  <div className="sm:col-span-5 max-w-[240px] mx-auto sm:max-w-none w-full">
                    <div className="relative aspect-[3/4] w-full overflow-hidden rounded-sm bg-muted border border-border/80 shadow-md transition-transform duration-250 ease-out group-hover:-translate-y-1">
                      {/* Realistic spine crease gradient overlay */}
                      <div className="pointer-events-none absolute inset-0 z-10 opacity-75" style={{
                    background: "linear-gradient(90deg, rgba(0,0,0,0.22) 0%, rgba(255,255,255,0.18) 3%, rgba(0,0,0,0.08) 7%, transparent 14%)"
                  }} aria-hidden="true" />
                      <img src={report.coverImage} data-wvc-srcset={report.coverSrcset} data-wvc-sizes={report.coverSizes} alt={`Cover for ${report.title}`} loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                      <div className="absolute top-2.5 right-2.5 z-20">
                        <Badge variant="secondary" className="rounded-xs bg-background/90 text-foreground text-[10px] font-mono backdrop-blur-xs border border-border">
                          {report.pagesCount}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Report Details & Metadata */}
                  <div className="sm:col-span-7 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex flex-wrap items-center gap-2 mb-2.5">
                        <Badge variant="outline" className="rounded-full bg-secondary/80 text-foreground border-transparent px-2.5 py-0.5 text-[11px] font-medium">
                          {report.category}
                        </Badge>
                        <span className="text-muted-foreground text-xs font-mono">
                          {report.publishDate}
                        </span>
                      </div>

                      <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground leading-snug tracking-tight mb-2">
                        {report.title}
                      </h3>

                      <p className="text-xs sm:text-sm font-medium text-muted-foreground mb-3 italic">
                        {report.subtitle}
                      </p>

                      <p className="text-sm text-foreground/80 leading-relaxed line-clamp-3 mb-6">
                        {report.excerpt}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-5 font-mono">
                        <span className="flex items-center gap-1.5">
                          <Clock className="size-3.5" />
                          {report.readTime}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <FileText className="size-3.5" />
                          {report.format}
                        </span>
                      </div>

                      <div className="flex items-center gap-3">
                        <Button asChild size="default" className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-sans font-medium px-5">
                          <Link to={report.href}>
                            <BookOpen className="size-4" />
                            <span>Read the report</span>
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.article>)}
          </div>

          {/* Right-Hand Column: Recent Research Briefings */}
          <motion.div variants={itemVariants} className="lg:col-span-4 rounded-md border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center justify-between pb-4 border-b border-border">
              <h4 className="font-serif text-lg font-semibold text-foreground">
                Recent Briefings
              </h4>
              <Badge variant="outline" className="rounded-sm text-[11px] font-mono text-muted-foreground">
                Weekly stream
              </Badge>
            </div>

            <div className="divide-y divide-border">
              {researchBriefings.map((briefing, i) => <div key={briefing.id} data-index={i} className="py-5 first:pt-4 last:pb-0 group">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                      {briefing.category}
                    </span>
                    <span className="text-[11px] font-mono text-muted-foreground flex items-center gap-1">
                      <SafeHydrate><Calendar className="size-3" /></SafeHydrate>
                      {briefing.date}
                    </span>
                  </div>

                  <h5 className="font-serif text-base font-medium text-foreground leading-snug mb-3 group-hover:text-primary transition-colors">
                    <Link to={briefing.href} className="hover:underline">
                      {briefing.title}
                    </Link>
                  </h5>

                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono text-muted-foreground">
                      PDF &middot; {briefing.fileSize}
                    </span>
                    <Button variant="ghost" size="sm" asChild className="h-7 px-2 text-xs font-medium text-foreground hover:text-foreground hover:bg-muted">
                      <Link to={briefing.href}>
                        <Download className="size-3.5 mr-1" />
                        <span>Download</span>
                      </Link>
                    </Button>
                  </div>
                </div>)}
            </div>

            <Separator className="my-6" />

            <div className="rounded-sm bg-muted/60 p-4 border border-border/80">
              <p className="text-xs font-medium text-foreground mb-1">
                Peer-reviewed methodology
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                All MinfoMedia quantitative surveys adhere to strict sample validation and independent verification standards.
              </p>
              <Button variant="link" size="sm" asChild className="p-0 h-auto text-xs font-semibold text-foreground">
                <Link to="/AboutUsPage">
                  <span>Review editorial policies</span>
                  <ArrowRight className="size-3 ml-1" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}